import { useState, useEffect } from 'react';
// @mui
import {
  Box,
  Card,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  styled,
  AvatarGroup,
} from '@mui/material';
// components
import Iconify from '../../../../components/Iconify';
import axios from '../../../../utils/axios';

// ----------------------------------------------------------------------

const HeaderCard = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.main,
  padding: theme.spacing(6, 3),
  borderRadius: '24px 24px 0 0',
  color: theme.palette.common.white,
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: -50,
    right: -50,
    width: 150,
    height: 150,
    borderRadius: '50%',
    background: alpha(theme.palette.common.white, 0.1),
  },
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.common.white,
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(2),
  },
}));

const ActionCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '24px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  boxShadow: (theme) => theme.customShadows.z8,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: (theme) => theme.customShadows.z16,
  },
}));

const FooterNav = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-around',
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: '0 0 24px 24px',
}));

export default function YangaSupportHub({ onStartChat }) {
  const [faqs, setFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get('/api/secure/faqs');
        if (response.data.status === 'success') {
          setFaqs(response.data.faqs);
        }
      } catch (error) {
        console.error('Fetch FAQs error:', error);
      }
    };
    fetchFaqs();
  }, []);

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Card sx={{ borderRadius: '24px', border: 'none', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', maxWidth: 500, mx: 'auto' }}>
      <HeaderCard>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center' }}>
          Hi there 👋
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9, mb: 4, fontWeight: 500, lineHeight: 1.5 }}>
          Need help? Search our help center for answers or start a conversation:
        </Typography>

        <SearchField
          fullWidth
          placeholder="Search for help"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </HeaderCard>

      <Box sx={{ p: 3, bgcolor: '#F8FAFC' }}>
        <ActionCard onClick={onStartChat}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Start a conversation
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AvatarGroup max={3}>
                <Avatar sx={{ bgcolor: 'primary.light' }}>A</Avatar>
                <Avatar sx={{ bgcolor: 'primary.dark' }}>M</Avatar>
              </AvatarGroup>
              <Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Our team typically replies in a few minutes
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Iconify icon="eva:message-circle-fill" />}
              sx={{ 
                borderRadius: '12px', 
                height: 56, 
                textTransform: 'none', 
                fontSize: '16px',
                fontWeight: 700,
                boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              New Conversation
            </Button>
          </Stack>
        </ActionCard>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 3, pb: 10, minHeight: 300 }}>
        <Typography variant="overline" sx={{ color: 'text.disabled', mb: 2, display: 'block', px: 1 }}>
          Frequently Asked Questions
        </Typography>
        
        <Stack spacing={1}>
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq) => (
              <Accordion 
                key={faq.id} 
                sx={{ 
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px !important',
                  mb: 1
                }}
              >
                <AccordionSummary expandIcon={<Iconify icon="eva:chevron-down-fill" />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
              No results found for "{searchQuery}"
            </Typography>
          )}
        </Stack>
      </Box>

      <FooterNav sx={{ flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Stack direction="row" justifyContent="space-around" sx={{ width: '100%' }}>
          <IconButton sx={{ color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}>
            <Iconify icon="eva:home-fill" width={24} height={24} />
          </IconButton>
          <IconButton onClick={onStartChat}>
            <Iconify icon="eva:message-circle-fill" width={24} height={24} color="text.secondary" />
          </IconButton>
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Iconify icon="eva:flash-fill" width={12} /> Powered by Amtpay
        </Typography>
      </FooterNav>
    </Card>
  );
}
